/**
 * useFreeFitLimit — RC 1.38.A
 *
 * Single, reusable, fallback-safe source for the free-tier daily
 * Analyze-Fit limit (the "N free job analyses for today" copy).
 *
 * Calls `GET /api/jobs/analyze-fit/usage` once per mount and caches
 * the resolved `limit` on the module so subsequent mounts within the
 * same session don't re-fetch.
 *
 * Backend contract (see `core.free_tier.get_analyze_fit_usage`):
 *   {
 *     "limit":        <int>,    // <-- this is what we surface
 *     "used":         <int>,
 *     "remaining":    <int>,
 *     "reset_at_utc": "<ISO Z>",
 *     "plan":         "free"
 *   }
 *
 * Fallback semantics:
 *   * No network / 4xx / 5xx / shape mismatch → returns the in-code
 *     default `2` so the UI never renders "undefined free job
 *     analyses".
 *   * The hook is read-only: it intentionally does not surface
 *     `used` / `remaining` — surfaces that need those continue to
 *     fetch the endpoint directly (they need real-time values, not
 *     a cached one).
 */
import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';

const FALLBACK_LIMIT = 2;
const ENDPOINT = '/api/jobs/analyze-fit/usage';

// Module-level cache. A single GET per session is plenty — the value
// only changes if an admin tweaks `app_settings.limits.free_daily_analyze_fit`,
// which requires a fresh login anyway.
let _cachedLimit = null;
let _inFlight = null;

async function _fetchLimit() {
  if (_cachedLimit != null) return _cachedLimit;
  if (_inFlight) return _inFlight;
  _inFlight = (async () => {
    try {
      const res = await apiFetch(ENDPOINT);
      const n = Number(res?.limit);
      _cachedLimit = Number.isFinite(n) && n > 0 ? Math.floor(n) : FALLBACK_LIMIT;
    } catch (_e) {
      _cachedLimit = FALLBACK_LIMIT;
    } finally {
      _inFlight = null;
    }
    return _cachedLimit;
  })();
  return _inFlight;
}

export function useFreeFitLimit() {
  const [limit, setLimit] = useState(_cachedLimit ?? FALLBACK_LIMIT);

  useEffect(() => {
    let alive = true;
    _fetchLimit().then((v) => {
      if (alive) setLimit(v);
    });
    return () => { alive = false; };
  }, []);

  return { limit };
}

// Test-only: clear the module cache. Not exported on the public path.
export const __resetFreeFitLimitCacheForTests = () => {
  _cachedLimit = null;
  _inFlight = null;
};
